import React, { ReactNode } from "react";
import styled from "@emotion/styled";
import { DateTime } from "luxon";
import { Flex, Text } from "styles/utility-components";

import { currencyFormatLong } from "components/app_layout/Typography";
import TooltipComponent from "components/ui/tooltip/Tooltip";
import { CurrencyEnum } from "lib/types/graphQLEnums";

type AttributeDisplayProps = {
  attribute: string;
  money?: {
    amount?: number | null;
    currency?: CurrencyEnum | null;
  } | null;
  date?: string | null;
  customValue?: ReactNode;
  attributeConfig?: {
    header?: boolean;
    tooltip?: string;
    tag?: {
      label: string;
      tooltip: string;
    };
  };
  leftColumnWidth?: "small" | "large";
};

export default function AttributeDisplay({
  attribute,
  money,
  date,
  customValue,
  attributeConfig,
  leftColumnWidth = "small",
}: AttributeDisplayProps) {
  if (!date && !money?.amount && !customValue) {
    return null;
  }
  return (
    <Flex gap="16px" alignItems="baseline">
      <LeftColumn leftColumnWidth={leftColumnWidth}>
        {attributeConfig?.tooltip ? (
          <TooltipComponent content={attributeConfig.tooltip}>
            <Text
              color="sysTextSecondary"
              h3={attributeConfig?.header}
              inline={!attributeConfig?.header}
            >
              {attribute}
            </Text>
          </TooltipComponent>
        ) : (
          <Text
            color="sysTextSecondary"
            h3={attributeConfig?.header}
            inline={!attributeConfig?.header}
          >
            {attribute}
          </Text>
        )}
        {attributeConfig?.tag && (
          <TooltipComponent content={attributeConfig.tag.tooltip}>
            <Tag>{attributeConfig.tag.label}</Tag>
          </TooltipComponent>
        )}
      </LeftColumn>
      <RightColumn leftColumnWidth={leftColumnWidth}>
        {date && <Text>{DateTime.fromISO(date).toFormat("dd LLL yyyy")}</Text>}
        {money?.amount && <Text>{currencyFormatLong(money.amount, money.currency ?? "GBP")}</Text>}
        {customValue}
      </RightColumn>
    </Flex>
  );
}

const LeftColumn = styled.div<{ leftColumnWidth: "small" | "large" }>(({ leftColumnWidth }) => ({
  width: leftColumnWidth === "small" ? "96px" : "150px",
  flexShrink: 0,
  display: "flex",
  gap: "8px",
  alignItems: "center",
}));

const RightColumn = styled.div<{ leftColumnWidth: "small" | "large" }>(({ leftColumnWidth }) => ({
  // 16px is from gap
  width: leftColumnWidth === "small" ? "calc(100% - 96px - 16px)" : "calc(100% - 150px - 16px)",
  flexShrink: 1,
}));

const Tag = styled.span(({ theme }) => ({
  backgroundColor: theme.colors.sysBackgroundAlternative,
  border: "1px solid",
  borderColor: theme.colors.sysBorderDecorative,
  borderRadius: "4px",
  padding: "0px 4px",
}));
